import React from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ReportPreviewManager } from '../../modules/reports/ReportPreviewManager';

interface ReportsGenerateSearch {
  /** Lock the generator to one campaign (Team view "+Report"). */
  campaignId?: string;
  sellerId?: string;
  sellerName?: string;
  category?: 'sellers';
  /** Preselect a campaign template (e.g. 'campaign-export' from the card Export action). */
  template?: string;
  /** Lock the generator to one lead (ISR "+Report"); implies lead mode. */
  leadId?: string;
  /** Where the user came from (set by the entry points); Close returns here. */
  from?: string;
}

export const Route = createFileRoute('/reports/generate')({
  validateSearch: (search: Record<string, unknown>): ReportsGenerateSearch => ({
    sellerId: (typeof search.sellerId === 'string' || typeof search.sellerId === 'number') && /^[\w-]{1,36}$/.test(String(search.sellerId)) ? String(search.sellerId) : undefined,
    sellerName: typeof search.sellerName === 'string' ? search.sellerName.slice(0,200) : undefined,
    category: search.category === 'sellers' ? 'sellers' : undefined,
    campaignId: typeof search.campaignId === 'string' && /^[\w-]{1,36}$/.test(search.campaignId) ? search.campaignId : undefined,
    template: typeof search.template === 'string' && /^[\w-]{1,36}$/.test(search.template) ? search.template : undefined,
    leadId: typeof search.leadId === 'string' && /^[\w-]{1,36}$/.test(search.leadId) ? search.leadId : undefined,
    // `from` may carry a QUERY STRING (the ISR restore params
    // ?openLeadId=…&openCampaignId=…, or ?view=team), so the charset must
    // include ?=&%. The old [\w\-/] charset silently rejected every from with
    // restore params — the param vanished and Close landed on /campaigns.
    from: typeof search.from === 'string' && /^[/][\w\-/?=&%.]{0,200}$/.test(search.from) ? search.from : undefined,
  }),
  // Reports generation is available to every signed-in user — the root route
  // has already resolved the session. Server-side authorization still happens
  // in the n8n-proxy on every action. The page renders WITHOUT the platform
  // AppLayout (credits/user header) — the generator is a focused full-screen
  // view; its own Close button returns to the caller's route.
  beforeLoad: ({ context }) => {
    if (!context.auth?.user) throw redirect({ to: '/login' });
  },
  component: ReportsGeneratePage,
});

function ReportsGeneratePage() {
  const { campaignId, leadId, from, template, sellerId, sellerName, category } = Route.useSearch();
  const navigate = useNavigate();
  const close = () => {
    // `from` encodes the caller's view INCLUDING restore params (e.g. the ISR
    // drawer to re-open). Direct-URL opens without `from` fall back to campaigns.
    if (from) navigate({ href: from });
    else navigate({ to: '/campaigns' });
  };
  return (
    <ReportPreviewManager
      sellerId={sellerId}
      sellerName={sellerName}
      initialCategory={category}
      lockCampaignId={campaignId}
      lockLeadId={leadId}
      initialCampaignTemplate={template as never}
      onBack={close}
    />
  );
}
